import { Menu, Dropdown } from 'antd';
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Menu as MenuIcon, Clipboard, Edit3, Settings, ArrowRight, ArrowDown,
    Plus, History, PlusCircle, BarChart3, FileText, Download, Clock5
} from 'lucide-react';  // Lucide icons

import useClipboard from "../../../../hooks/useClipboard";
import usePortfolioActions from "../../usePortfolioActions";
import {downloadMonthlyTimeseries} from "../../../../api/timeseriesApi";

const PortfolioRowMenuContent = ({ menu }) => {
    return (
        <>
            <Dropdown overlay={menu} trigger={['click']}>
                <MenuIcon style={{ fontSize: '18px' }} />
            </Dropdown>
        </>
    );
};



/**
 * PortfolioRowMenu Component
 *
 * @param {function} onEdit - Function to handle the edit action
 * @param {object} portfolio - Portfolio object document
 * @returns {JSX.Element}
 * @constructor
 */
const PortfolioRowMenu = React.memo(({ onEdit, portfolio }) => {
    const userId = localStorage.getItem('uid');
    const navigate = useNavigate();
    const copyToClipboard = useClipboard();

    const { handlePortfolioAction } = usePortfolioActions({ portfolio });

    const portfolioUserId = portfolio?.attr?.create_uid;
    const isExistingSma = portfolio.hasOwnProperty('properties') && portfolio.properties.hasOwnProperty('sma_config');

    // Memoize menu items
    const menuItems = useMemo(() => {
        const items = [
            {
                key: 'open',
                label: 'Open in New Tab',
                icon: <ArrowRight size={20} />,
                onClick: () => window.open(`/public/portfolio/${portfolio.id}`),
            },
            {
                key: 'view',
                label: 'View in Page',
                icon: <ArrowDown size={20} />,
                onClick: () => navigate(`/portfolio/${portfolio.id}`),
            },
            {
                key: 'copy',
                label: "Copy ID",
                icon: <Clipboard size={20} />,
                onClick: () => copyToClipboard(portfolio.id),
            },
            {
                key: 'positions',
                label: 'Signal History',
                icon: <History size={20} />,
                onClick: () => navigate(`/signals/${portfolio.id}/history`),
            },
            {
                key: "etfmatrix",
                label: "ETF Matrix",
                icon: <BarChart3 size={20} />,
                onClick: ()=>{
                    handlePortfolioAction('etfMatrix')
                }
            },
            {
                key: 'sma',
                label: isExistingSma ? 'Edit SMA' : 'Create SMA',
                icon: <Plus size={20} />,
                onClick: () => handlePortfolioAction(isExistingSma ? 'editSma' : 'createSma'),
            },
            {
                key: 'factSheet',
                label: 'Fact Sheet',
                icon: <FileText size={20} />,
                onClick: () => navigate(`/portfolio/${portfolio.id}/factsheet`),
            },
            {
                key: 'download',
                label: 'Download Monthly CSV',
                icon: <Download size={20} />,
                onClick: () => downloadMonthlyTimeseries(portfolio.id),
            },

        ];

        if (portfolioUserId && portfolioUserId === userId) {
            items.unshift(
                {
                    key: 'updatePositions',
                    label: 'Update Positions',
                    icon: <Settings size={20} />,
                    onClick: () => handlePortfolioAction('updatePositions'),
                },
                {
                    key: "edits",
                    label: "Edit",
                    icon: <Edit3 size={20} />,
                    onClick: onEdit,
                }
            );
        }

        return items;
    }, [
        portfolio.id,
        isExistingSma,
        portfolioUserId,
        userId,
        navigate,
        copyToClipboard,
        handlePortfolioAction,
        onEdit
    ]);

    // Memoize menu component
    const menu = useMemo(() => (
        <Menu>
            {menuItems.map(item => (
                <Menu.Item key={item.key} icon={item.icon} onClick={item.onClick}>
                    {item.label}
                </Menu.Item>
            ))}
        </Menu>
    ), [menuItems]);


    return (
        <PortfolioRowMenuContent menu={menu}/>
    );
});

// Add displayName for debugging

export default PortfolioRowMenu;